import { Typography, darken, lighten, useTheme } from "@mui/material"
import React from "react"
import ReactApexChart from "react-apexcharts"

import { applyLegendFlex, getValue } from "../../utils/general/generalUtils"
import {
    LCT_DISPLAY_NAME_DISPATCHER,
    RESOLVED_STATUS_DISPATCHER,
} from "../../utils/reporting/reportingGeneralUtils"
import { FIXED_CHART_HEIGHT } from "../../webpages/dnoPortal/dnoHomepage"
import { AppCardHeader } from "../surfaces/appCard"
import AppPaper from "../surfaces/appPaper"

interface Status {
    [key: string]: number
}

interface Category {
    [key: string]: Status
}

export interface DataStacked {
    totLctsByResolvedStatus: Category
}

interface ChartProps {
    title: string
    description: string
    data: DataStacked
}

/**
 * A stacked bar chart component that displays data.
 *
 * @component
 * @param {string} title - The title of the chart.
 * @param {string} description - The description of the chart.
 * @param {DataStacked[]} data - An array of DayData objects representing the data to be displayed on the chart.
 * @returns {JSX.Element} The rendered stacked bar chart component.
 */
const BarChart: React.FC<ChartProps> = ({ title, description, data }) => {
    const theme = useTheme()

    const colors = [
        darken(theme.palette.primary.main, 0.5),
        theme.palette.primary.main,
        lighten(theme.palette.primary.main, 0.6),
        darken(theme.palette.primary.light, 0.5),
        theme.palette.primary.light,
        lighten(theme.palette.primary.light, 0.6),
        darken(theme.palette.primary.dark, 0.5),
        theme.palette.primary.dark,
        lighten(theme.palette.primary.dark, 0.6),
    ]

    const categories = Object.keys(data?.totLctsByResolvedStatus ?? {})
    const statuses = new Set<string>()

    categories.forEach((category) => {
        Object.keys(data?.totLctsByResolvedStatus?.[category] ?? {}).forEach(
            (status) => {
                statuses.add(status)
            }
        )
    })

    const series = categories.map((category) => ({
        name: getValue(LCT_DISPLAY_NAME_DISPATCHER, category) ?? category,
        data: Array.from(statuses).map(
            (status) => data?.totLctsByResolvedStatus?.[category]?.[status] || 0
        ),
    }))

    const statusesDisplay = Array.from(statuses).map(
        (status) => getValue(RESOLVED_STATUS_DISPATCHER, status) ?? status
    )

    return (
        <AppPaper
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <AppCardHeader
                title={title}
                subheader={description}
                titleTypographyProps={{
                    variant: "h5",
                    fontWeight: "500",
                }}
            />

            {series.length === 0 ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 4 }}
                >
                    No data available
                </Typography>
            ) : (
                <ReactApexChart
                    height={FIXED_CHART_HEIGHT}
                    options={{
                        chart: {
                            type: "bar",
                            stacked: true,
                            toolbar: {
                                show: false,
                            },
                            events: {
                                mounted: applyLegendFlex,
                                updated: applyLegendFlex,
                            },
                        },
                        legend: {
                            horizontalAlign: "center",
                            itemMargin: {
                                horizontal: 5,
                                vertical: 4,
                            },
                            markers: {
                                offsetX: -2,
                            },
                            labels: {
                                colors: theme.palette.text.secondary,
                            },
                        },
                        plotOptions: {
                            bar: {
                                horizontal: false,
                                columnWidth: "25",

                                borderRadius: 8,
                                borderRadiusApplication: "end",
                            },
                        },
                        xaxis: {
                            type: "category",
                            categories: Array.from(statusesDisplay),
                            labels: {
                                rotate: 0,
                                style: {
                                    colors: theme.palette.text.secondary,
                                },
                            },
                        },
                        yaxis: {
                            labels: {
                                style: {
                                    colors: theme.palette.text.secondary,
                                },
                            },
                        },
                        grid: { show: false },
                        colors,
                    }}
                    series={series}
                    type="bar"
                    sx={{ height: "100%", flexGrow: 1 }}
                />
            )}
        </AppPaper>
    )
}

export default BarChart
